<template>
	<div id="vueApp" class="no-select" v-cloak>
		<div class="pagination-container" v-show="showPaginator">
			<input type="button" class="pagination-button" :class="classFirstPage" :title="titleFirstPage" v-show="showFirstLastPageButton" @click="setPageIndex(0)" value="&lt;&lt;&lt;" :disabled="suppressFirstPageButton" />
			<input type="button" class="pagination-button" :class="classPreviousPageSet" :title="titlePreviousPageSet" v-show="showNextPreviousPageSetButton" @click="setPageIndex(pageIndex - pageSetSize)" value="&lt;&lt;" :disabled="suppressPreviousPageSetButton" />
			<input type="button" class="pagination-button" :class="classPreviousPage" :title="titlePreviousPage" v-show="showNextPreviousPageButton" @click="setPageIndex(pageIndex - 1)" value="&lt;" :disabled="suppressPreviousPageButton" />
			<input type="button" v-for="pageIndex in currentPageIndexSet" :key="pageIndex" class="pagination-button" :title="titlePageIndex(pageIndex + 1)" v-show="showPageIndexButtons" :class="classSelectionStatus(pageIndex)" @click="setPageIndex(pageIndex)" :value="pageIndex + 1" />
			<input type="button" class="pagination-button" :class="classNextPage" :title="titleNextPage" v-show="showNextPreviousPageButton" @click="setPageIndex(pageIndex + 1)" value="&gt;" :disabled="suppressNextPageButton" />
			<input type="button" class="pagination-button" :class="classNextPageSet" :title="titleNextPageSet" v-show="showNextPreviousPageSetButton" @click="setPageIndex(pageIndex + pageSetSize)" value="&gt;&gt;" :disabled="suppressNextPageSetButton" />
			<input type="button" class="pagination-button" :class="classLastPage" :title="titleLastPage" v-show="showFirstLastPageButton" @click="setPageIndex(nrPages - 1)" value="&gt;&gt;&gt;" :disabled="suppressLastPageButton" />
			<div class="disable-layer" v-show="disabled"></div>
		</div>
		<div class="feedback" v-show="showFeedback">
			<span>{{feedback}}</span>
		</div>
	</div>
</template>




<script>
	// todo: make it a grid to get rid of double borders and no zero-borders in mobile view
	// https://stackoverflow.com/questions/47882924/preventing-double-borders-in-css-grid

	export default {
		name: 'Paginator',
		emits: ['update:modelValue', 'paginate'],
		data: () => ({
			pageIndex : 0
		}),
		props: {
			modelValue    : { type: Number, required: false, default: 0 },
			nrRecords     : { type: Number, required: true, default: 0 },
			pageSize      : { type: Number, required: true, default: 0 },
			pageSetSize   : { type: Number, required: false, default: 10 },
			disabled      : { type: Boolean, required: false, default: false },
			hidePaginator : { type: Boolean, required: false, default: false },
			options       : { type: Object, required: false, default: () => ({}) }
		},
		methods: {
			rangeCappedPageIndex(pageIndex) {
				return (this.nrPages > 0)
					? pageIndex.rangeCap(0, this.nrPages - 1)
					: 0;
			},
			setPageIndex(pageIndex) {
				pageIndex = this.rangeCappedPageIndex(pageIndex);
				var diffValues = (this.pageIndex != pageIndex);

				if (diffValues) {
					this.pageIndex = pageIndex;
					this.emitPageIndex();
				}
			},
			classSelectionStatus(pageIndex) {
				return {
					'selected' : (pageIndex == this.pageIndex)
				};
			},
			emitPageIndex() {
				this.$emit('update:modelValue', this.pageIndex); // update v-modeled pageIndex
				this.$emit('paginate', this.pageIndex); // also send pageIndex along with event
			},
			titlePageIndex(pageNumber) {
				return `Go to page ${pageNumber}`;
			}
		},
		computed: {
			nrPages() {
				return (this.pageSize > 0)
					? Math.ceil(this.nrRecords / this.pageSize)
					: 0;
			},
			pageIndexes() {
				var pageIndexes = [];

				for (var i = 0; i < this.nrPages; i++) {
					pageIndexes.push(i);
				}

				return pageIndexes;
			},
			currentPageIndexSet() {
				var begin = this.currentPageIndexSetIndex * this.pageSetSize;
				var end = begin + this.pageSetSize;
				end = (end <= this.pageIndexes.length)
					? end
					: this.pageIndexes.length;

				return this.pageIndexes.slice(begin, end);
			},
			currentPageIndexSetIndex() {
				return Math.floor(this.pageIndex / this.pageSetSize);
			},
			nrPageIndexSets() {
				return Math.ceil(this.nrRecords / this.pageSize / this.pageSetSize);
			},
			from() {
				return (this.pageIndex * this.pageSize) + 1;
			},
			to() {
				var to = (this.from + this.pageSize);
				return (to <= this.nrRecords)
					? to - 1
					: this.nrRecords;
			},
			showFeedback() {
				return (this.feedback != null);
			},
			feedback() {
				return (this.nrPages > 1)
					? this.feedbackMultiPage
					: (this.nrPages == 1) && (this.nrRecords > 1)
					? this.feedbackSinglePage
					: (this.nrPages == 1) && (this.nrRecords == 1)
					? this.feedbackSingleItem
					: (this.nrRecords == 0)
					? this.feedbackSinglePage
					: null;
			},
			feedbackMultiPage() {
				var feedback = this.options?.texts?.countMultiPage ?? null;
				return (feedback != null)
					? feedback.replace('{from}', this.from).replace('{to}', this.to).replace('{count}', this.nrRecords)
					: null;
			},
			feedbackSinglePage() {
				var feedback = this.options?.texts?.countSinglePage ?? null;
				return (feedback != null)
					? feedback.replace('{count}', this.nrRecords)
					: null;
			},
			feedbackSingleItem() {
				return this.options?.texts?.countSingleItem?? null;
			},
			titleFirstPage() {
				return 'Go to first page';
			},
			titlePreviousPageSet() {
				return `Skip back ${this.pageSetSize} pages`;
			},
			titlePreviousPage() {
				return 'Previous page';
			},
			titleNextPage() {
				return 'Next page';
			},
			titleNextPageSet() {
				return `Skip ahead ${this.pageSetSize} pages`;
			},
			titleLastPage() {
				return 'Go to last page';
			},
			classFirstPage() {
				return {
					'suppressed-button' : this.suppressFirstPageButton,
					'left-most' : this.showFirstLastPageButton
				};
			},
			classPreviousPageSet() {
				return {
					'suppressed-button' : this.suppressPreviousPageSetButton,
					'left-most' : (this.showNextPreviousPageSetButton && !this.showFirstLastPageButton)
				};
			},
			classPreviousPage() {
				return {
					'suppressed-button' : this.suppressPreviousPageButton,
					'left-most' : (this.showNextPreviousPageButton && !this.showNextPreviousPageSetButton && !this.showFirstLastPageButton)
				};
			},
			classNextPage() {
				return {
					'suppressed-button' : this.suppressNextPageButton,
					'right-most' : (this.showNextPreviousPageButton && !this.showNextPreviousPageSetButton && !this.showFirstLastPageButton)
				};
			},
			classNextPageSet() {
				return {
					'suppressed-button' : this.suppressNextPageSetButton,
					'right-most' : (this.showNextPreviousPageSetButton && !this.showFirstLastPageButton)
				};
			},
			classLastPage() {
				return {
					'suppressed-button' : this.suppressLastPageButton,
					'right-most' : this.showFirstLastPageButton
				};
			},
			suppressFirstPageButton() {
				return (this.currentPageIndexSetIndex == 0);
			},
			suppressPreviousPageSetButton() {
				return (this.currentPageIndexSetIndex == 0);
			},
			suppressPreviousPageButton() {
				return (this.pageIndex == 0);
			},
			suppressNextPageButton() {
				return (this.pageIndex == (this.nrPages - 1));
			},
			suppressNextPageSetButton() {
				return (this.currentPageIndexSetIndex == (this.nrPageIndexSets - 1));
			},
			suppressLastPageButton() {
				return (this.currentPageIndexSetIndex == (this.nrPageIndexSets - 1));
			},
			showFirstLastPageButton() {
				return (this.nrPageIndexSets > 1);
			},
			showNextPreviousPageSetButton() {
				return (this.nrPageIndexSets > 1);
			},
			showNextPreviousPageButton() {
				return (this.nrPages > 1);
			},
			showPageIndexButtons() {
				return (this.nrPages > 1);
			},
			showPaginator() {
				return !(this?.hidePaginator ?? false);
			}
		},
		watch: {
			modelValue(newVal, oldVal) {
				this.pageIndex = newVal;
			},
			nrPages(newVal, oldVal) {
				this.pageIndex = this.rangeCappedPageIndex(this.modelValue);
			}
		}
	};
</script>




<style scoped>
	[v-cloak] {
		display: none;
	}

	#vueApp {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		border: 0px solid black;
	}

	.pagination-container {
		border: 0px solid black;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		position: relative;
	}

	.pagination-button {
		border-top: 1px solid #dee2e6;
		border-right: 1px solid #dee2e6;
		border-bottom: 1px solid #dee2e6;
		border-left: 1px solid #dee2e6;
		min-width: 40px;
		height: 40px;
		padding: 0px 10px;
		background: white;
		color: rgb(67,56,202);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}

	.pagination-button:hover {
		background: #e9ecef;
	}

	.pagination-button.left-most {
		border-top-left-radius: 0.5rem;
		border-bottom-left-radius: 0.5rem;
	}

	.pagination-button.right-most {
		border-top-right-radius: 0.5rem;
		border-bottom-right-radius: 0.5rem;
	}

	.suppressed-button {
		cursor: default;
		color: #dee2e6;
		background: white !important;
	}

	.feedback {
		margin-top: 10px;
	}

	.disable-layer {
		position: absolute;
		top: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		background: black;
		opacity: 0.025;
		cursor: not-allowed;
	}

	.selected {
		background: rgb(67,56,202) !important;
		color: white;
	}
</style>